import { useEffect } from "react";
import { matchPath, useLocation } from "react-router-dom";

const APP_NAME = "KAPsurvey";

// Ordered list — the first pattern that matches the current pathname wins,
// so more specific patterns must be listed before their parent paths
// (e.g. "/automated-campaigns/distribute" before "/automated-campaigns/:id").
const PAGE_TITLES = [
   // Auth
   { path: "/auth/login", title: "Login" },
   { path: "/auth/registration", title: "Registration" },
   { path: "/auth/forgot-password", title: "Forgot Password" },
   { path: "/auth/success", title: "Success" },
   { path: "/auth/request", title: "Request" },
   { path: "/auth/freeTrial/successful", title: "Free Trial" },

   // Public
   { path: "/public-survey/feedback/:uuid/:id", title: "Survey" },
   { path: "/public-survey/:uuid/:id", title: "Survey" },

   // Organization / space / project selection
   { path: "/organizations", title: "Organizations" },
   { path: "/space-selection", title: "Spaces" },
   { path: "/project-selection/create-project/:id", title: "Create Project" },
   { path: "/project-selection/create-project", title: "Create Project" },
   { path: "/project-selection", title: "Projects" },

   // Admin
   { path: "/admin-workspaces", title: "Workspaces" },
   { path: "/admin-user-management/new-member", title: "New Member" },
   { path: "/admin-user-management", title: "Team" },
   { path: "/admin-deleted-items", title: "Deleted Items" },
   { path: "/admin-payment-plans", title: "Payment Plans" },
   { path: "/admin/admin-organization-management", title: "Organization Management" },
   { path: "/workspace/new-work-space", title: "New Workspace" },
   { path: "/workspace/edit/:id", title: "Edit Workspace" },

   // Account settings
   { path: "/billing/card-saved", title: "Billing" },
   { path: "/billing", title: "Billing" },
   { path: "/subscription-plans", title: "Subscription" },
   { path: "/usage", title: "Usage" },
   { path: "/balance", title: "Balance" },
   { path: "/profile", title: "Profile" },
   { path: "/login-information", title: "Login & Security" },
   { path: "/tickets/:id", title: "Ticket Details" },
   { path: "/tickets", title: "Tickets" },

   // Workspace / project
   { path: "/workspace", title: "Workspace" },
   { path: "/mytasks", title: "My Tasks" },
   { path: "/tasks", title: "My Tasks" },
   { path: "/action-plan/*", title: "Action Plan" },
   { path: "/calls", title: "Call History" },
   { path: "/projects", title: "Projects" },
   { path: "/team", title: "Team" },
   { path: "/deleted-items", title: "Deleted Items" },
   { path: "/dashboard/edit-project", title: "Edit Project" },
   { path: "/dashboard", title: "Project Dashboard" },

   // Surveys
   { path: "/surveys", title: "Surveys" },
   { path: "/survey/import", title: "Import Survey" },
   { path: "/survey/create-survey/:id", title: "Create Survey" },
   { path: "/survey/create-survey", title: "Create Survey" },
   { path: "/survey/test/:id", title: "Survey Preview" },
   { path: "/survey/submission/:id", title: "Submission" },
   { path: "/survey/highlights/:id", title: "Highlights" },
   { path: "/survey/edit/:id", title: "Survey Builder" },
   { path: "/survey/edit-survey-details/:id", title: "Edit Survey Details" },
   { path: "/survey/data/:id", title: "Survey Data" },
   { path: "/survey-analytics/:id", title: "Survey Analytics" },
   { path: "/survey-analytics", title: "Survey Analytics" },

   // Audience
   { path: "/audience/:id", title: "Audience" },
   { path: "/audience", title: "Audience" },
   { path: "/relationships", title: "Relationships" },
   { path: "/quick-actions", title: "Quick Actions" },

   // Tasks & campaigns
   { path: "/task-management/managers-dashboard", title: "Managers Dashboard" },
   { path: "/task-management", title: "Task Management" },
   { path: "/assignments/add", title: "New Assignment" },
   { path: "/assignments", title: "Assignments" },
   { path: "/automated-campaigns/distribute", title: "Distribute Survey" },
   { path: "/automated-campaigns/draft/:id", title: "Distribute Survey" },
   { path: "/automated-campaigns/:id/sends/:sendId", title: "Send Details" },
   { path: "/automated-campaigns/:id", title: "Campaign Details" },
   { path: "/automated-campaigns", title: "Automated Campaigns" },

   // Errors / misc
   { path: "/coming-soon", title: "Coming Soon" },
   { path: "/unauthorized", title: "Unauthorized" },
   { path: "/404", title: "Page Not Found" },
   { path: "/500", title: "Error" }
];

/**
 * Keeps the browser tab title in sync with the active route.
 * Mount once inside the router (see Routes.js).
 */
export function usePageTitle() {
   const { pathname } = useLocation();

   useEffect(() => {
      const match = PAGE_TITLES.find((page) => matchPath(page.path, pathname));
      document.title = match ? `${match.title} | ${APP_NAME}` : APP_NAME;
   }, [pathname]);
}
